import Script from 'next/script';
import { useRef, useState } from 'react';

import { useReferralInfo } from '@flipster/features/src/shared/referral-code/state';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { css } from '@flipster/styled-system/css';
import { alert } from '@flipster/ui/Alert';
import AppleIcon from '@flipster/ui/assets/svgs/social/ic-apple.svg';
import GoogleIcon from '@flipster/ui/assets/svgs/social/ic-google.svg';
import segment, { ReferralEvent } from '@flipster/utils/segment';

import { useGoogleSignInButton } from '@web/app/[locale]/(web)/_auth/use-google-sign-in-button';
import { useSocialLogin } from '@web/hooks/useSocialLogin';

interface Props {
  onClick?: () => void;
  disabled?: boolean;
}

export const ContinueWithGoogle = ({ onClick, disabled }: Props) => {
  const googleRef = useRef<HTMLDivElement | null>(null);
  const { ready, isInAppBrowser } = useGoogleSignInButton(googleRef);

  const onClickContinueWithGoogle = () => {
    onClick?.();

    if (!isInAppBrowser) return;
    alert.show({
      title: 'Error',
      message:
        'Please access flipster.io on a separate\nbrowser such as Google Chrome, to\naccess social sign-in or sign-up.',
      confirmText: 'Dismiss',
    });
  };

  return (
    <IconButton
      hierarchy='tertiary'
      size='large'
      icon={
        <>
          <GoogleIcon width={20} height={20} />
          <div
            className={css({
              pos: 'absolute',
              top: 0,
              left: 0,
              w: '100%',
              minW: 0,
              transform: 'scaleY(1.5)',
              opacity: 0.0001,
            })}
            ref={googleRef}
          />
        </>
      }
      css={{ pos: 'relative', overflow: 'hidden' }}
      onClick={onClickContinueWithGoogle}
      disabled={(!ready && !isInAppBrowser) || disabled}
    />
  );
};

interface Props {
  onClick?: () => void;
  disabled?: boolean;
}

/*
 * data type
 * {
 *   "authorization": {
 *     "code": "[CODE]",
 *     "id_token": "[ID_TOKEN]",
 *     "state": "[STATE]"
 *   },
 * }
 */

export const ContinueWithApple = ({ onClick, disabled }: Props) => {
  const referralInfo = useReferralInfo();
  const [load, setLoad] = useState<boolean>(false);

  const loginHandler = useSocialLogin();

  const onClickLogin = async () => {
    onClick?.();

    try {
      // https://developer.apple.com/documentation/signinwithapplejs/clientconfigi/usepopup
      await window.AppleID?.auth?.init({
        clientId: 'com.aqx',
        redirectURI: `https://${window.location.hostname}`,
        scope: 'email',
        usePopup: true,
      });
      const data = await window.AppleID?.auth.signIn();
      await loginHandler('APPLE', data.authorization.id_token);
    } catch (err) {
      // oxlint-disable-next-line no-console
      console.error(err);
    }
    segment.track(ReferralEvent.SignInSsoClick, {
      auth_type: 'apple',
      ...referralInfo,
    });
  };

  return (
    <>
      <Script
        id='apple-auth-script'
        src='https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
        onReady={() => setLoad(true)}
      />
      <IconButton
        hierarchy='tertiary'
        size='large'
        icon={<AppleIcon width={20} height={20} />}
        onClick={onClickLogin}
        disabled={!load || disabled}
      />
    </>
  );
};
